package ca.northroot.app.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.material3.*
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp

private val DarkColorScheme = darkColorScheme(
    primary = NorthrootSageGreen,
    onPrimary = NorthrootDeepForest,
    primaryContainer = NorthrootForestGreen,
    onPrimaryContainer = NorthrootWhite,
    secondary = NorthrootGold,
    onSecondary = NorthrootDeepForest,
    tertiary = NorthrootLightGray,
    onTertiary = NorthrootBlack,
    error = NorthrootRed,
    onError = Color.White,
    background = NorthrootDeepForest,
    onBackground = NorthrootWhite,
    // Polished Dark Mode: Brighter surfaces for better card depth
    surface = Color(0xFF243B2F), 
    onSurface = NorthrootWhite,
    surfaceVariant = Color(0xFF1B2B24),
    onSurfaceVariant = NorthrootSageGreen,
    outline = NorthrootWhite.copy(alpha = 0.12f),
)

private val LightColorScheme = lightColorScheme(
    primary = NorthrootForestGreen,
    onPrimary = Color.White,
    primaryContainer = NorthrootForestGreen.copy(alpha = 0.1f),
    onPrimaryContainer = NorthrootForestGreen,
    secondary = NorthrootGold,
    onSecondary = Color.White,
    tertiary = NorthrootSageGreen,
    onTertiary = Color.White,
    error = NorthrootRed,
    onError = NorthrootWhite,
    background = NorthrootBackground,
    onBackground = NorthrootDeepForest,
    surface = Color.White,
    onSurface = NorthrootDeepForest,
    surfaceVariant = Color(0xFFEDF2F4),
    onSurfaceVariant = NorthrootForestGreen,
    outline = NorthrootForestGreen.copy(alpha = 0.12f),
)

enum class ThemeMode {
    LIGHT, DARK, SYSTEM
}

@Composable
fun NorthrootTheme(
    themeMode: ThemeMode = ThemeMode.SYSTEM,
    content: @Composable () -> Unit,
) {
    val darkTheme = when (themeMode) {
        ThemeMode.LIGHT -> false
        ThemeMode.DARK -> true
        ThemeMode.SYSTEM -> isSystemInDarkTheme()
    }

    val colorScheme = if (darkTheme) DarkColorScheme else LightColorScheme

    MaterialTheme(
        colorScheme = colorScheme,
        typography = Typography,
        content = content,
    )
}

@Composable
fun ThemeSelectionDialog(
    currentMode: ThemeMode,
    onModeChange: (ThemeMode) -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("App Theme") },
        text = {
            Column {
                ThemeOption("System Default", ThemeMode.SYSTEM, currentMode, onModeChange)
                ThemeOption("Light Mode", ThemeMode.LIGHT, currentMode, onModeChange)
                ThemeOption("Dark Mode", ThemeMode.DARK, currentMode, onModeChange)
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { 
                Text("Close") 
            }
        },
    )
}

@Composable
fun ThemeOption(
    label: String,
    mode: ThemeMode,
    currentMode: ThemeMode,
    onSelect: (ThemeMode) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onSelect(mode) }
            .padding(vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        RadioButton(selected = mode == currentMode, onClick = { onSelect(mode) })
        Spacer(Modifier.width(8.dp))
        Text(label)
    }
}
